import * as _ from "lodash";
import {IdentityProvider} from "../PA.Core/Basic/Identity";
import {MessageProvider} from "../PA.Core/Basic/Messages";
import {ProgressProvider} from "../PA.Core/Basic/Progress";
import {IMessageProvider, IProgressProvider, MessageType} from "../PA.Core/PA.Core.IF.Basic";
import {IBusinessObject} from "../PA.Core/PA.Core.IF.BO";
import {ESDataStore} from "../PA.Core/lib/ESDataStore";
import {ESRecord} from "../PA.Core/lib/ESRecord";
import {CreateOptions, ESRecordResolver} from "../PA.Core/lib/ESRecordResolver"
import {PAdbContext} from "../PA.Core/PAdbContext";
import {
    esWB_ControlPoint,
    esWB_ControlPoint_Type,
    esWB_ControlPoint_Type_Process,
    esWB_ControlPoint_Type_Process_Input
} from "../PA.Core/PAdbSchema";
import {
    WB_ControlPointRow,
    WB_ControlPointTypeProcessInputRow,
    WB_ControlPointTypeProcessRow,
    WB_ControlPointTypeRow
} from "../PA.Core/PAdbSchemaRows";
import {IwbControlPointType} from "../PA.Core/PAdbSchemaRowsIF";
import {PAEnvironment} from "../PA.Core/PAEnvironment";

export class ControlPointTypeBO
    implements
        IBusinessObject
{

    //<editor-fold desc = "Properties and Constructor">
    Identity: IdentityProvider;

    CreateOptions: any;

    LoadOptions: any;
    _bIsLoaded: boolean;
    _OnLoadComplete: Promise<IBusinessObject>;
    LoadProgressProvider: IProgressProvider;

    ValidateOption: any;
    _bIsValidated: boolean;
    ValidateMessageProvider: IMessageProvider;
    ValidateProgressProvider: IProgressProvider;

    SaveOptions: any;
    _bIsSaved: boolean;
    SaveProgressProvider: IProgressProvider;

    DeleteOptions: any;
    _bIsDeleted: boolean;
    DeleteMessageProvider: IMessageProvider;
    DeleteProgressProvider: IProgressProvider;

    dbContext: PAdbContext;
    drWB_ControlPointType: WB_ControlPointTypeRow;

    constructor(options?: any) {
        this.CreateOptions = options || {};
        if ('dbContext' in this.CreateOptions) {
            this.dbContext = this.CreateOptions.dbContext;
        }

        this.Identity = new IdentityProvider({id: 0, name: 'New Control Point Type'});

        this.LoadOptions = {};
        this._bIsLoaded = false;
        this._OnLoadComplete = null;
        this.LoadProgressProvider = new ProgressProvider('Loading', this);

        this.ValidateOption = {};
        this._bIsValidated = false;
        this.ValidateMessageProvider = new MessageProvider();
        this.ValidateProgressProvider = new ProgressProvider('Validating', this);

        this.SaveOptions = {};
        this._bIsSaved = false;
        this.SaveProgressProvider = new ProgressProvider('Saving', this);

        this.DeleteOptions = {};
        this._bIsDeleted = false;
        this.DeleteMessageProvider = new MessageProvider();
        this.DeleteProgressProvider = new ProgressProvider('Deleting', this);

        this.drWB_ControlPointType = null;
    }

    //</editor-fold>

    get WB_ControlPointType(): WB_ControlPointTypeRow {
        return this.drWB_ControlPointType;
    }

    //<editor-fold desc = "IBusinessObject operations">

    LoadNew(options?: object): Promise<IBusinessObject> {
        const self = this;

        self.LoadProgressProvider.start("Creating Control Point Type");
        let onLoadComplete = self._OnLoadComplete = new Promise<IBusinessObject>((resolve, reject) => {
            self.LoadOptions = options || {};

            if ('dbContext' in self.LoadOptions) {
                self.dbContext = self.LoadOptions.dbContext;
            } else {
                self.dbContext = new PAdbContext();
            }

            if (this.dbContext == null) {
                const sError = 'Unable to determine dbContext for new Control Point Type.';
                self._OnLoadComplete = null;
                self.LoadProgressProvider.exception(sError);
                reject(sError);
                return;
            }

            this.dbContext.createRecordWithKey(esWB_ControlPoint_Type)
                .then((aNewRow: WB_ControlPointTypeRow) => {
                    aNewRow.setDefaults();
                    self.drWB_ControlPointType = aNewRow;
                    self.dbContext.add(esWB_ControlPoint_Type, aNewRow);
                    self.drWB_ControlPointType = aNewRow;
                    self.Identity.setAll(aNewRow.cif.WB_CONTROL_POINTM_ID, aNewRow.cif.CONTROL_POINT_TYPE_ID, aNewRow.cif.CONTROL_POINT_TYPE_NAME);
                    self.LoadProgressProvider.setName('Loading ' + this.Identity.name + '(' + this.Identity.id_ui + ')'
                    );
                    this.Identity.listenToRecordUpdates(self.drWB_ControlPointType,
                        'WB_CONTROL_POINTM_ID',
                        'CONTROL_POINT_TYPE_ID',
                        'CONTROL_POINT_TYPE_NAME');


                    self.LoadProgressProvider.finish();
                    self._bIsLoaded = true;
                    self._OnLoadComplete = null;
                    resolve(self);
                })
                .catch((err) => {
                    self.LoadProgressProvider.exception(err);
                    reject(err);
                });
        });

        return onLoadComplete;
    }

    Load(anId: number | string, options?: object): Promise<IBusinessObject> {
        const self = this;
        let recordResolver: ESRecordResolver;
        let recordResolverLoadPromise: Promise<void>;

        self.LoadProgressProvider.start('Loading (' + self.Identity.id_ui + ')');
        this._OnLoadComplete = new Promise<IBusinessObject>((resolve, reject) => {
            if (self.bIsLoaded) {
                const sError = 'Control Point Type already loaded.';
                self._OnLoadComplete = null;
                self.LoadProgressProvider.exception(sError);
                reject(sError);
                return;
            }
            self.LoadOptions = options || {};

            if ('dbContext' in self.LoadOptions) {
                self.dbContext = self.LoadOptions.dbContext;
            } else if (self.dbContext == null) {
                self.dbContext = new PAdbContext();
            }

            if (this.dbContext == null) {
                const sError = 'Unable to determine dbContext for Control Point Type.';
                self._OnLoadComplete = null;
                self.LoadProgressProvider.exception(sError);
                reject(sError);
                return;
            }

            if ('RecordResolver' in self.LoadOptions) {
                recordResolver = (<any> self.LoadOptions).RecordResolver;
                recordResolverLoadPromise = Promise.resolve()
            } else {
                recordResolver = ControlPointTypeBO.createRecordResolver({ dbContext: self.dbContext });
                recordResolverLoadPromise = ControlPointTypeBO.setupRecordResolver(recordResolver, [anId]);
            }

            recordResolverLoadPromise
                .then(() => {
                    const aLoadRow: WB_ControlPointTypeRow = <any> recordResolver.get(esWB_ControlPoint_Type, anId);

                    if (_.isEmpty(aLoadRow)) {
                        self._OnLoadComplete = null;
                        self.LoadProgressProvider.exception('Id not found.');
                        resolve(self);
                        return;
                    }

                    self.drWB_ControlPointType = aLoadRow;
                    self.Identity.setAll(aLoadRow.cif.WB_CONTROL_POINTM_ID, aLoadRow.cif.CONTROL_POINT_TYPE_ID, aLoadRow.cif.CONTROL_POINT_TYPE_NAME);
                    self.LoadProgressProvider.setName('Loading ' + this.Identity.name + '(' + this.Identity.id_ui + ')'
                    );
                    this.Identity.listenToRecordUpdates(self.drWB_ControlPointType,
                        'WB_CONTROL_POINTM_ID',
                        'CONTROL_POINT_TYPE_ID',
                        'CONTROL_POINT_TYPE_NAME');

                })
                .then(() => {
                    self._bIsLoaded = true;
                    self._OnLoadComplete = null;
                    self.LoadProgressProvider.finish();
                    resolve(self);
                })
                .catch((err: any) => {
                    self._OnLoadComplete = null;
                    self.LoadProgressProvider.exception(err);
                    reject(err);
                });
        });

        return this._OnLoadComplete;
    }


    get OnLoadComplete(): Promise<IBusinessObject> {
        return this._OnLoadComplete;
    }

    get bIsLoaded(): boolean {
        return this._bIsLoaded;
    }

    async Validate(options?: any): Promise<IBusinessObject> {
        const self = this;
        self._bIsValidated = true
        self.ValidateMessageProvider.clearMessages();
        self.ValidateProgressProvider.start('Validating ' + self.WB_ControlPointType.cif.CONTROL_POINT_TYPE_NAME + '(' + self.WB_ControlPointType.cif.CONTROL_POINT_TYPE_ID + ')' )
        self.ValidateOption = options || {};

        if (self.dbContext == undefined) {
            const sError = 'Unable to determine dbContext to validate Control Point Type.';
            self.ValidateProgressProvider.exception(sError);
            self.ValidateMessageProvider.addMessage({type: MessageType.Error, title: sError, description: sError, code: "dbContext"})
            self._bIsValidated = false;
            return self;
        }

        if (_.isEmpty(self.WB_ControlPointType.cif.CONTROL_POINT_TYPE_ID)){
            let sError = "Control Point Type Id cannot be empty"
            self.ValidateMessageProvider.addMessage({type: MessageType.Error, title: sError, description: sError, code: "Id"})
        }

        if (_.isEmpty(self.WB_ControlPointType.cif.CONTROL_POINT_TYPE_NAME)){
            let sError = "Control Point Type Name cannot be empty"
            self.ValidateMessageProvider.addMessage({type: MessageType.Error, title: sError, description: sError, code: "Name"})
        }

        if(_.isEmpty(self.WB_ControlPointType.cif.ENTERPRISE_POLICY)){
            let sError = "Enterprise Policy cannot be empty"
            self.ValidateMessageProvider.addMessage({type: MessageType.Error, title: sError, description: sError, code: "EnterprisePolicy"})
        }

        let controlPointRows: WB_ControlPointRow[] = await self.dbContext.findAll(esWB_ControlPoint,
            {where: {DELETED_IND: {neq: '1'},
                    WB_CONTROL_POINTM_ID: self.WB_ControlPointType.cif.WB_CONTROL_POINTM_ID,
                    }})

        if(controlPointRows.length>0){
            let sError="Cannot save Enterprise Policy changes since there are Control Point definition(s) using this Control Point Type. Please delete all related Control Point definitions in order to save changes to the Control Point Type Enterprise Policy"
            self.ValidateMessageProvider.addMessage({type: MessageType.Error, title: sError, description: sError, code: "controlPointsPresent"})

        }


        _.forEach(self.WB_ControlPointType.WB_ControlPointTypeProcessRows, (aProcessRow) => {
            if(!aProcessRow.isActive)
                return

            if(_.isEmpty(aProcessRow.cif.PROCESS_ID)){
                let sError = "Control Point Process Id cannot be empty"
                self.ValidateMessageProvider.addMessage({type: MessageType.Error, title: sError, description: sError, code: "ProcessId"})
            }

            if(_.isEmpty(aProcessRow.cif.PROCESS_NAME)){
                let sError = "Control Point Process Name cannot be empty"
                self.ValidateMessageProvider.addMessage({type: MessageType.Error, title: sError, description: sError, code: "ProcessName"})
            }



            // For Each Process Input
            _.forEach(aProcessRow.WB_ControlPointTypeProcessInputRows, (anInputRow) => {
                if(!anInputRow.isActive)
                    return

                if(_.isEmpty(anInputRow.cif.INPUT_NAME)){
                    let sError = `Control Point Process Input Name cannot be empty, Process: ${aProcessRow.cif.PROCESS_NAME}`
                    self.ValidateMessageProvider.addMessage({type: MessageType.Error, title: sError, description: sError, code: "ProcessInputName"})
                }

            })

        })

        self._bIsValidated = (self.ValidateMessageProvider.countMessages(MessageType.Error) == 0);
        self.ValidateProgressProvider.finish();
        return self;
    }

    get bIsValidated(): boolean {
        return this._bIsValidated;
    }

    Save(options?: any): Promise<IBusinessObject> {
        return this.SaveUsing(false, options);
    }

    SaveBatch(options?: any): Promise<IBusinessObject> {
        return this.SaveUsing(true, options);
    }

    private async SaveUsing(bBatch: boolean, options?: any): Promise<IBusinessObject> {
        const self = this;

        try {
            const wbControlPointType = <IwbControlPointType>self.drWB_ControlPointType;
            this.SaveProgressProvider.start(
                'Saving ' + wbControlPointType.CONTROL_POINT_TYPE_NAME + '(' + wbControlPointType.CONTROL_POINT_TYPE_ID + ')'
            );

            if (self.dbContext == null) {
                self._bIsSaved = false;
                const sError = 'Unable to determine dbContext to Save Control Point Type.';
                self.SaveProgressProvider.exception(sError);
                return self;
            }

            self.SaveOptions = options || {};

            await self.Validate(options)
            if(!self.bIsValidated){
                self._bIsSaved = false;
                self.SaveProgressProvider.finish()
                return
            }

            let saveFunction: (esName: string, saveRecords: ESRecord[] | ESRecord, options?: any) => Promise<ESDataStore>;

            if (bBatch) {
                saveFunction = self.dbContext.SaveBatch.bind(self.dbContext);
            } else {
                saveFunction = self.dbContext.Save.bind(self.dbContext);
            }

            await saveFunction(esWB_ControlPoint_Type, self.drWB_ControlPointType, {})
            await saveFunction(esWB_ControlPoint_Type_Process, self.drWB_ControlPointType.WB_ControlPointTypeProcessRows, {})
            let processInputRows = _.flatMap(self.drWB_ControlPointType.WB_ControlPointTypeProcessRows, (a) => a.WB_ControlPointTypeProcessInputRows)
            await saveFunction(esWB_ControlPoint_Type_Process_Input, processInputRows)

            if (bBatch) {
                self.dbContext.addBatchCompleteListener(() => {
                    self._bIsSaved = true;
                });
            } else {
                self._bIsSaved = true;
            }

            self.SaveProgressProvider.finish();

        } catch (err) {
            self.SaveProgressProvider.exception(err);
        }

        return self
    }

    get bIsSaved(): boolean {
        return this._bIsSaved;
    }

    Delete(options?: any): Promise<IBusinessObject> {
        return this.DeleteUsing(true, options);
    }

    DeleteOnSave(options?: any): Promise<IBusinessObject> {
        return this.DeleteUsing(false, options);
    }

    private async DeleteUsing(bSaveNow: boolean, options?: any): Promise<IBusinessObject> {
        const self = this;
        const wbControlPointType = <IwbControlPointType> self.drWB_ControlPointType;

        try {

        self.DeleteMessageProvider.clearMessages();
        self.DeleteProgressProvider.start(
            'Deleting ' + wbControlPointType.CONTROL_POINT_TYPE_NAME + '(' + wbControlPointType.CONTROL_POINT_TYPE_ID + ')'
        );

        self.drWB_ControlPointType.markDeleted();

        if (bSaveNow)
           await self.SaveUsing(false)

        self._bIsDeleted = true;
        self.DeleteProgressProvider.finish();
        } catch (err) {
            self.DeleteProgressProvider.exception(err)
        }

        return self
    }

    get bIsDeleted(): boolean {
        return this._bIsDeleted;
    }

    //</editor-fold>

    //<editor-fold desc = "Process/ Input operations">

    async addProcess(): Promise<WB_ControlPointTypeProcessRow>
    {
        const self = this

        let cptProcess
            = <WB_ControlPointTypeProcessRow> await self.dbContext.createRecordWithKey(esWB_ControlPoint_Type_Process)
        cptProcess.setDefaults()
        cptProcess.cif.WB_CONTROL_POINTM_ID = self.drWB_ControlPointType.cif.WB_CONTROL_POINTM_ID
        self.dbContext.add(esWB_ControlPoint_Type_Process, cptProcess)
        return cptProcess
    }

    async removeProcess(wbControlPointPRId: number): Promise<void>
    {
        const self = this

        _.forEach(self.drWB_ControlPointType.WB_ControlPointTypeProcessRows, (aRow) => {
            if(!aRow.isActive)
                return

            if(aRow.cif.WB_CONTROL_POINTMPR_ID == wbControlPointPRId) {
                aRow.markDeleted()
                _.forEach(aRow.WB_ControlPointTypeProcessInputRows, (aInputRow) => {
                    if(!aInputRow.isActive)
                        return

                    aInputRow.markDeleted()
                })
            }
        })

    }

    async addProcessInput(wbControlPointPRId: number): Promise<WB_ControlPointTypeProcessInputRow>
    {
        const self = this

        let cptProcess = _.first(_.filter(self.drWB_ControlPointType.WB_ControlPointTypeProcessRows,
                                 (aRow) => aRow.cif.WB_CONTROL_POINTMPR_ID == wbControlPointPRId))

       if(_.isEmpty(cptProcess))
           return undefined

        let cptProcessInput
            = <WB_ControlPointTypeProcessInputRow> await self.dbContext.createRecordWithKey(esWB_ControlPoint_Type_Process_Input)
        cptProcessInput.setDefaults()
        cptProcessInput.cif.WB_CONTROL_POINTMPR_ID = cptProcess.cif.WB_CONTROL_POINTMPR_ID
        self.dbContext.add(esWB_ControlPoint_Type_Process_Input, cptProcessInput)
        return cptProcessInput
    }

    async removeProcessInput(wbControlPointPIRId: number): Promise<void> {
        const self = this

        let foundRec: WB_ControlPointTypeProcessInputRow = undefined

        _.forEach(self.drWB_ControlPointType.WB_ControlPointTypeProcessRows, (aRow) => {
            if(!aRow.isActive)
                return

            _.forEach(aRow.WB_ControlPointTypeProcessInputRows, (aInputRow) => {
                if(!aInputRow.isActive)
                    return
                if(aInputRow.cif.WB_CONTROL_POINTMPIR_ID == wbControlPointPIRId){
                    aInputRow.markDeleted()
                    foundRec = aInputRow
                    return false // stop iterating
                }
            })

            if(foundRec)
                return false // stop iterating
        })

    }

    //</editor-fold>

    //<editor-fold desc = "IRecord Resolver operations <static>">

    static createRecordResolver(boLoadOptions: any): ESRecordResolver {
        const opts = boLoadOptions || {};
        let dbContext: PAdbContext;
        if ('dbContext' in opts) {
            dbContext = opts.dbContext;
        } else {
            dbContext = new PAdbContext({});
        }
        const controlPointType_options: CreateOptions = {
            standardConditions: {
                [esWB_ControlPoint_Type]: {"DELETED_IND": {"notIn": ['1']}},
                [esWB_ControlPoint_Type_Process]: {"DELETED_IND": {"notIn": ['1']}},
                [esWB_ControlPoint_Type_Process_Input]: {"DELETED_IND": {"notIn": ['1']}}
            }
            ,
            dctIncludeBelongsToNamedRelations: {}
        }

        return new ESRecordResolver(dbContext, controlPointType_options);
    }

    static setupRecordResolver(recordResolver: ESRecordResolver, ids: (string | number)[]): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            recordResolver.findAllWithRelationships(esWB_ControlPoint_Type, ids)
                .then(() => {
                    resolve();
                })
                .catch((err) => {
                    reject(err);
                });
        });
    }


    //</editor-fold>

}

PAEnvironment.registerBusinessObjectClass(ControlPointTypeBO, "ControlPointTypeBO", "Control Point Type")
